import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { PASSING_GRADE } from '../../../shared/constant/grading';
import {
  ABSENT_RATE_THRESHOLD,
  MIN_RECORDED_DAYS,
  NEAR_FAILING_MARGIN,
  isStillAddressed,
} from '../../Dashboard/services/attentionService';

const LEVELS = {
  danger: { dot: 'bg-red-500', tone: 'bg-red-50 text-red-600' },
  warning: { dot: 'bg-amber-500', tone: 'bg-amber-50 text-amber-600' },
};

// Whether a teacher has marked the subject's grade as addressed, and the mark still holds: the same
// check as on the teacher's dashboard (a later term or a lower grade brings the flag back).
function gradeAddressed(reviews, subjectId, term, grade) {
  return reviews.some(
    (r) => r.reason === 'failing' && r.subjectId === subjectId && isStillAddressed(r, { term, grade })
  );
}

// Whether the subject's teacher has marked the student's absences as addressed. That mark covers
// the absences across all of the teacher's subjects, and holds until there are more of them.
function absencesAddressed(reviews, subjectId, attendance) {
  return reviews.some((r) => {
    if (r.reason !== 'absenteeism' || !r.subjectIds.includes(subjectId)) return false;
    const absent = attendance
      .filter((row) => r.subjectIds.includes(row.subjectId))
      .reduce((sum, row) => sum + row.absent, 0);
    return isStillAddressed(r, { absent });
  });
}

// The subjects that need the student's attention in the selected term, using the same thresholds
// teachers see on their dashboard: a failing or nearly failing term grade, and frequent absences.
// Flags a teacher has marked as addressed are left out.
function buildAlerts(subjects, grades, attendance, reviews, term) {
  const alerts = [];

  subjects.forEach((subject) => {
    const grade = grades[`${subject.subjectId}|${term}`];
    const addressed = grade != null && gradeAddressed(reviews, subject.subjectId, Number(term), grade);
    if (grade != null && grade < PASSING_GRADE && !addressed) {
      alerts.push({ subject, level: 'danger', label: 'Failing', detail: `Grade ${grade}, below the passing mark of ${PASSING_GRADE}` });
    } else if (grade != null && grade >= PASSING_GRADE && grade < PASSING_GRADE + NEAR_FAILING_MARGIN && !addressed) {
      alerts.push({ subject, level: 'warning', label: 'Nearly failing', detail: `Grade ${grade}, close to the passing mark of ${PASSING_GRADE}` });
    }

    const row = attendance.find((r) => r.subjectId === subject.subjectId && r.term === Number(term));
    if (row && !absencesAddressed(reviews, subject.subjectId, attendance)) {
      const counted = row.present + row.late + row.absent;
      if (counted >= MIN_RECORDED_DAYS && row.absent / counted >= ABSENT_RATE_THRESHOLD) {
        alerts.push({
          subject,
          level: 'warning',
          label: 'Frequent absences',
          detail: `Absent ${row.absent} of ${counted} days (${Math.round((row.absent / counted) * 100)}%)`,
        });
      }
    }
  });

  // Failing first, then warnings; each group stays in subject order.
  return alerts.sort((a, b) => (a.level === b.level ? 0 : a.level === 'danger' ? -1 : 1));
}

function StudentAlerts({ subjects, grades, attendance, attentionReviews = [], term }) {
  const alerts = buildAlerts(subjects, grades, attendance, attentionReviews, term);
  if (alerts.length === 0) return null;

  const hasDanger = alerts.some((a) => a.level === 'danger');

  return (
    <div
      className={`rounded-lg border p-4 space-y-3 ${hasDanger ? 'border-red-200 bg-red-50/60' : 'border-amber-200 bg-amber-50/60'}`}
      role="alert"
    >
      <p className={`flex items-center gap-2 font-semibold ${hasDanger ? 'text-red-700' : 'text-amber-700'}`}>
        <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
        Needs attention
      </p>

      <ul className="space-y-2">
        {alerts.map((alert) => {
          const level = LEVELS[alert.level];
          return (
            <li key={`${alert.subject.subjectId}-${alert.label}`}>
              <Link
                to={`/student-dashboard/subjects/${alert.subject.subjectId}`}
                className="flex items-center justify-between gap-3 rounded-md bg-white border border-gray-200 px-3 py-2 hover:border-[#4ea6d0]"
              >
                <div className="min-w-0 flex items-start gap-2">
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${level.dot}`} aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#0b4a71] truncate">{alert.subject.name}</p>
                    <p className="text-xs text-gray-500">{alert.detail}</p>
                  </div>
                </div>
                <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${level.tone}`}>{alert.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default StudentAlerts;
